import React, { useMemo } from 'react';

import type { UnitType } from '@r6digital/rapidstor-sdk/domain';
import type Location from '@r6digital/rapidstor-types/react/entities/Location';

import PriceInfo from '@/standard-unit-listing/components/unit-type/PriceInfo';
import classes from '@/standard-unit-listing/components/unit-type/styles.module.scss';

import { useSite } from '../../sdk/useSite';

export interface DealPriceCardsProps {
    unitType: UnitType;
    location: Location;
    /** Currently selected deal id, or `null` when the standard price is selected. */
    selectedDealId: string | null;
    onSelectDeal: (dealId: string | null) => void;
}

/**
 * The stack of selectable price cards: one per deal, plus a plain standard-rate card when the unit
 * has no deals.
 *
 * Each card prices its deal with `effectiveRateAgainst(unitType.rate)`, so the discount is measured
 * against the rate of the unit it belongs to rather than the deal's own baseline.
 *
 * Every figure here is rendered by a **site-bound** formatter — `site.formatRate`,
 * `unitType.formatBaseRate`, `site.periodSuffix` — never by concatenating a symbol onto a number. That
 * is what makes the site's `currencySymbol` *and* its `currencySymbolPositionOverride` apply for free:
 * a Korean site rendering the won as `원` after the amount gets `90,000원/mo` here with no code in this
 * component knowing which side it goes on. The original screen concatenates, so it cannot.
 */
export default function DealPriceCards({
    unitType,
    location,
    selectedDealId,
    onSelectDeal,
}: DealPriceCardsProps) {
    const site = useSite(location);
    const { deals, rate } = unitType;

    // The amount and its period render in separate elements, so the suffix is needed on its own.
    // `periodSuffix` splits it off whichever side the currency symbol sits on, so pairing it with a
    // `shortForm` amount recomposes into exactly what `formatRate()` would have returned.
    const periodSuffix = useMemo(() => site.periodSuffix(), [site]);

    const selectable = deals.length > 1;

    return (
        <div className={classes.priceInfoStack}>
            {deals.map((deal) => {
                const effective = deal.effectiveRateAgainst(rate);

                return (
                    <PriceInfo
                        key={deal.id}
                        discountLabel={deal.name.trim() || deal.label()}
                        price={site.formatRate(effective, { shortForm: true })}
                        period={periodSuffix}
                        originalPrice={
                            effective.lessThan(rate) ? unitType.formatBaseRate() : undefined
                        }
                        active={selectedDealId === deal.id}
                        selectable={selectable}
                        onSelect={() => onSelectDeal(deal.id)}
                    />
                );
            })}

            {deals.isEmpty ? (
                <PriceInfo
                    borderless
                    // Base rate, not `formatRate()`, to match the original screen: with no deal it
                    // showed the unit's pre-discount figure. See MISSING-FROM-SDK.md.
                    price={unitType.formatBaseRate({ shortForm: true })}
                    period={periodSuffix}
                />
            ) : null}
        </div>
    );
}
